import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Alert } from "@mui/material";
import { AppAutocomplete, AppButton, AppTextField } from "../form";
import { AppFormField, AppFormGrid, AppSlideOver } from "../layout";
import { GRID_FULL } from "../../lib/ui/formLayout";
import { recipientDomainNotAllowedText } from "../../utils/notification";
import {
  dashboardExportScheduleService,
  type ExportDateRangePreset,
  type ExportScheduleFrequency,
} from "../../services/dashboardExportScheduleService";

type Props = {
  open: boolean;
  dashboardId: string;
  dashboardName?: string;
  onClose: () => void;
};

type FrequencyOption = { value: ExportScheduleFrequency; label: string };
type PresetOption = { value: ExportDateRangePreset; label: string };

export default function ExportScheduleDialog({ open, dashboardId, dashboardName, onClose }: Props) {
  const { t } = useTranslation();
  const [emails, setEmails] = useState("");
  const [frequency, setFrequency] = useState<ExportScheduleFrequency>("WEEKLY");
  const [preset, setPreset] = useState<ExportDateRangePreset>("LAST_7D");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorWarning, setErrorWarning] = useState(false);

  const frequencyOptions = useMemo<FrequencyOption[]>(
    () => [
      { value: "DAILY", label: t("export.frequencyDaily") },
      { value: "WEEKLY", label: t("export.frequencyWeekly") },
    ],
    [t]
  );

  const presetOptions = useMemo<PresetOption[]>(
    () => [
      { value: "LAST_7D", label: t("dateRange.last7days") },
      { value: "LAST_30D", label: t("dateRange.last30days") },
      { value: "THIS_MONTH", label: t("dateRange.thisMonth") },
    ],
    [t]
  );

  const save = async () => {
    setSaving(true);
    setError(null);
    setErrorWarning(false);
    try {
      await dashboardExportScheduleService.create({
        dashboardId,
        dashboardName,
        frequency,
        dateRangePreset: preset,
        hourUtc: 8,
        dayOfWeek: "MONDAY",
        recipientEmails: emails.split(",").map((e) => e.trim()).filter(Boolean),
        active: true,
      });
      onClose();
    } catch (e: unknown) {
      const warning = recipientDomainNotAllowedText((e as { response?: { data?: unknown } })?.response?.data);
      const message = e instanceof Error ? e.message : String(e);
      setErrorWarning(Boolean(warning));
      setError(warning || message || t("export.scheduleFailed"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <AppSlideOver
      open={open}
      onClose={onClose}
      size="sm"
      title={t("export.scheduleTitle")}
      actions={
        <>
          <AppButton variant="outlined" onClick={onClose}>
            {t("common.cancel")}
          </AppButton>
          <AppButton onClick={() => void save()} disabled={saving || !emails.trim()}>
            {saving ? t("common.loading") : t("common.save")}
          </AppButton>
        </>
      }
    >
      {error && (
        <Alert severity={errorWarning ? "warning" : "error"} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      <Alert severity="info" sx={{ mb: 2 }}>
        {t("export.scheduleMailHint")}
      </Alert>
      <AppFormGrid>
        <AppFormField size={GRID_FULL}>
          <AppTextField
            label={t("export.scheduleEmails")}
            value={emails}
            onChange={(e) => setEmails(e.target.value)}
            placeholder={t("export.emailPlaceholder")}
          />
        </AppFormField>
        <AppFormField size={GRID_FULL}>
          <AppAutocomplete<FrequencyOption>
            label={t("export.scheduleFrequency")}
            options={frequencyOptions}
            value={frequencyOptions.find((o) => o.value === frequency) ?? frequencyOptions[1]}
            onChange={(_, opt) => opt && setFrequency(opt.value)}
            getOptionLabel={(o) => o.label}
            isOptionEqualToValue={(a, b) => a.value === b.value}
            disableClearable
          />
        </AppFormField>
        <AppFormField size={GRID_FULL}>
          <AppAutocomplete<PresetOption>
            label={t("export.scheduleDateRange")}
            options={presetOptions}
            value={presetOptions.find((o) => o.value === preset) ?? presetOptions[0]}
            onChange={(_, opt) => opt && setPreset(opt.value)}
            getOptionLabel={(o) => o.label}
            isOptionEqualToValue={(a, b) => a.value === b.value}
            disableClearable
          />
        </AppFormField>
      </AppFormGrid>
    </AppSlideOver>
  );
}
